import { RefCallback, useCallback, useEffect, useRef, useState } from 'react';

interface IntersectionObserverInit {
    root?: Element | Document | null;
    rootMargin?: string;
    threshold?: number | number[];
}

type IntersectionObserverProps = {
    observerOptions?: IntersectionObserverInit;
    disabled?: boolean;
} & (
    | {
          intersectingCallback?: never;
          nonIntersectingCallback: () => void;
      }
    | {
          intersectingCallback: () => void;
          nonIntersectingCallback?: never;
      }
    | {
          intersectingCallback: (entry: IntersectionObserverEntry) => void;
          nonIntersectingCallback: () => void;
      }
);

/**
 * Reports when an element enters or leaves the viewport.
 *
 * Attach the returned ref to the element to watch: `<div ref={ref} />`.
 * React calls it whenever that element mounts, changes or unmounts, so the
 * element can render after a loading state or inside a child component.
 */
export function useIntersectionObserver({
    intersectingCallback,
    nonIntersectingCallback,
    observerOptions,
    disabled,
}: IntersectionObserverProps): RefCallback<Element> {
    const intersectingCallbackRef = useRef<
        ((entry: IntersectionObserverEntry) => void) | undefined
    >(undefined);
    const nonIntersectingCallbackRef = useRef<(() => void) | undefined>(
        undefined
    );
    const hasIntersectingCallback = !!intersectingCallback;
    const hasNonIntersectingCallback = !!nonIntersectingCallback;
    // Store the latest callback in a ref to avoid triggering useEffect on every render
    intersectingCallbackRef.current = intersectingCallback;
    nonIntersectingCallbackRef.current = nonIntersectingCallback;

    const [_observerOptions] = useState(
        observerOptions ?? { rootMargin: '20px' }
    );
    const observerRef = useRef<IntersectionObserver | undefined>(undefined);
    const targetRef = useRef<Element | null>(null);

    useEffect(() => {
        if (disabled) return;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && hasIntersectingCallback) {
                    intersectingCallbackRef.current?.(entry);
                } else if (
                    !entry.isIntersecting &&
                    hasNonIntersectingCallback
                ) {
                    nonIntersectingCallbackRef.current?.();
                }
            });
        }, _observerOptions);
        observerRef.current = observer;
        // A target attached in the first commit is stored before this runs.
        if (targetRef.current) observer.observe(targetRef.current);
        return () => {
            observer.disconnect();
            observerRef.current = undefined;
        };
    }, [
        _observerOptions,
        disabled,
        hasIntersectingCallback,
        hasNonIntersectingCallback,
    ]);

    return useCallback((node: Element | null) => {
        const observer = observerRef.current;
        if (observer && targetRef.current) {
            observer.unobserve(targetRef.current);
        }
        targetRef.current = node;
        if (observer && node) observer.observe(node);
    }, []);
}
